/**
 * grab.js — grabbables registry, hold, and the throw velocity estimator.
 *
 * This is the module Design Pillar 1 lives in ("The Throw Must Feel Perfect").
 * Every tunable comes from the active stall's throw profile (constants.js
 * THROW_PROFILES), never from a literal in here.
 *
 * Velocity estimation, per GDD 5.3, in order:
 *   1. Ring-buffer the hand's world position every frame while holding.
 *   2. On release, discard the last `releaseFrameOffset` samples — the frames
 *      around a grip release are contaminated by the release motion itself.
 *   3. Differentiate consecutive samples into per-frame velocities.
 *   4. Average the last `velocitySmoothFrames` of them with exponential
 *      recency weighting.
 *   5. Clamp to [minThrowSpeed, maxThrowSpeed].
 *   6. Apply aim assist: if a registered target lies within `aimAssistCone`
 *      degrees of the throw, rotate the velocity `aimAssistStrength` of the way
 *      toward it.
 *   7. Apply the profile's gravityScale to the thrown body.
 *
 * @module core/grab
 */

import * as THREE from 'three';
import {
  DEFAULT_THROW_PROFILE, VELOCITY_BUFFER_SIZE, HAND, EVENTS,
} from '../constants.js';
import { bus } from './events.js';
import { input } from './input.js';
import * as physics from './physics.js';
import { haptics } from './haptics.js';

/** Fixed-capacity chronological sample buffer. */
class SampleBuffer {
  constructor(capacity) {
    this.capacity = capacity;
    /** @type {Array<{p: THREE.Vector3, t: number}>} */
    this.items = [];
  }

  push(position, time) {
    this.items.push({ p: position.clone(), t: time });
    if (this.items.length > this.capacity) this.items.shift();
  }

  clear() { this.items.length = 0; }

  /** @returns {Array<{p: THREE.Vector3, t: number}>} Oldest first. */
  all() { return this.items; }
}

/**
 * @typedef {Object} GrabbableOptions
 * @property {THREE.Vector3} [holdOffset] Object position relative to the hand.
 * @property {THREE.Quaternion} [holdRotation] Object rotation relative to the hand.
 * @property {string} [kind] Free-form label ('ball', 'mallet', 'ring'…).
 * @property {boolean} [returnOnRelease=false] Snap back instead of being thrown.
 * @property {(h: import('./physics.js').BodyHandle, hand: object) => void} [onGrab]
 * @property {(h: import('./physics.js').BodyHandle, hand: object, v: THREE.Vector3) => void} [onRelease]
 */

class GrabSystem {
  constructor() {
    /** @type {Map<number, {handle: object, opts: GrabbableOptions}>} */
    this.grabbables = new Map();
    /** @type {{left: object|null, right: object|null}} Currently held handles. */
    this.held = { left: null, right: null };
    /** @type {{left: SampleBuffer, right: SampleBuffer}} */
    this.buffers = {
      left: new SampleBuffer(VELOCITY_BUFFER_SIZE),
      right: new SampleBuffer(VELOCITY_BUFFER_SIZE),
    };

    /** @type {import('../constants.js').ThrowProfile} */
    this.profile = { ...DEFAULT_THROW_PROFILE };
    this.stallId = null;
    /** @type {((releasePos: THREE.Vector3) => {position: THREE.Vector3}|null)|null} */
    this.targetFinder = null;

    this.enabled = true;

    this._scratch = {
      v: new THREE.Vector3(),
      v2: new THREE.Vector3(),
      q: new THREE.Quaternion(),
      axis: new THREE.Vector3(),
    };
  }

  /**
   * Make a physics body grabbable.
   * @param {import('./physics.js').BodyHandle} handle
   * @param {GrabbableOptions} [opts]
   */
  register(handle, opts = {}) {
    if (!handle) return;
    this.grabbables.set(handle.id, { handle, opts });
  }

  /** @param {import('./physics.js').BodyHandle} handle */
  unregister(handle) {
    if (!handle) return;
    this.grabbables.delete(handle.id);
    for (const side of ['left', 'right']) {
      if (this.held[side] && this.held[side].id === handle.id) this.held[side] = null;
    }
  }

  /** Drop everything. Stalls call this on teardown. */
  clear() {
    this.grabbables.clear();
    this.held.left = null;
    this.held.right = null;
    this.buffers.left.clear();
    this.buffers.right.clear();
  }

  /**
   * Install the active stall's throw profile (GDD 5.4).
   * @param {import('../constants.js').ThrowProfile} profile
   * @param {{ stallId?: string, targetFinder?: Function }} [opts]
   */
  setProfile(profile, { stallId = null, targetFinder = null } = {}) {
    this.profile = { ...DEFAULT_THROW_PROFILE, ...(profile || {}) };
    this.stallId = stallId;
    this.targetFinder = targetFinder;
  }

  /** Restore defaults when leaving a stall. */
  resetProfile() {
    this.profile = { ...DEFAULT_THROW_PROFILE };
    this.stallId = null;
    this.targetFinder = null;
  }

  /**
   * @param {'left'|'right'} side
   * @returns {object|null} The handle held in that hand.
   */
  heldBy(side) {
    return this.held[side];
  }

  /** @returns {boolean} */
  isHolding(side) {
    return !!this.held[side];
  }

  /** Per-frame: sample poses, service grab/release edges, move held bodies. */
  update(dt) {
    if (!this.enabled) return;
    const now = performance.now();

    for (const side of ['left', 'right']) {
      const hand = input.getHand(side);
      if (!hand) continue;

      this.buffers[side].push(hand.position, now);

      if (hand.gripJustPressed) this.tryGrab(side);
      if (hand.gripJustReleased && this.held[side]) this.release(side);

      const handle = this.held[side];
      if (handle && !handle.removed) this._moveHeld(handle, hand);
    }
  }

  /**
   * Attempt to grab the nearest registered body.
   * @param {'left'|'right'} side
   * @returns {object|null} The grabbed handle.
   */
  tryGrab(side) {
    if (this.held[side]) return this.held[side];
    const hand = input.getHand(side);
    if (!hand) return null;

    let best = null;
    let bestDist = HAND.grabRadius;
    for (const { handle } of this.grabbables.values()) {
      if (handle.removed) continue;
      if (this.held.left === handle || this.held.right === handle) continue;
      const d = handle.position().distanceTo(hand.position);
      if (d < bestDist) {
        bestDist = d;
        best = handle;
      }
    }
    if (!best) return null;
    return this.grab(side, best);
  }

  /**
   * Grab a specific handle. Used directly by dispensers that hand an object
   * straight into the player's hand.
   * @param {'left'|'right'} side
   * @param {object} handle
   * @returns {object|null}
   */
  grab(side, handle) {
    if (!handle || handle.removed) return null;
    const entry = this.grabbables.get(handle.id);
    this.held[side] = handle;
    this.buffers[side].clear();

    handle.setKinematic(true);
    handle.freeze();

    const hand = input.getHand(side);
    if (hand) this._moveHeld(handle, hand);

    haptics.play(side, 'grab');
    if (entry?.opts.onGrab) entry.opts.onGrab(handle, hand);
    bus.emit(EVENTS.GRAB, { side, handle, kind: entry?.opts.kind || null });
    return handle;
  }

  /**
   * Release whatever is in a hand, computing and applying the throw velocity.
   * @param {'left'|'right'} side
   * @param {THREE.Vector3} [velocityOverride] Bypass the estimator (test mode).
   * @returns {{ handle: object, velocity: THREE.Vector3 }|null}
   */
  release(side, velocityOverride = null) {
    const handle = this.held[side];
    if (!handle) return null;
    this.held[side] = null;

    const entry = this.grabbables.get(handle.id);
    const hand = input.getHand(side);
    const releasePos = handle.position();

    let velocity;
    if (velocityOverride) {
      velocity = velocityOverride.clone();
    } else {
      velocity = this._estimateVelocity(side);
      velocity = this._clampSpeed(velocity);
      velocity = this._applyAimAssist(velocity, releasePos);
    }

    if (handle.removed) return null;

    handle.setKinematic(false);
    handle.setGravityScale(this.profile.gravityScale ?? 1);
    handle.setVelocity(velocity);
    // A little spin reads as a real throw rather than a slide.
    handle.setAngularVelocity({
      x: velocity.z * 0.35, y: 0, z: -velocity.x * 0.35,
    });

    this.buffers[side].clear();
    haptics.play(side, 'release');

    if (entry?.opts.onRelease) entry.opts.onRelease(handle, hand, velocity);
    bus.emit(EVENTS.RELEASE, { side, handle, velocity: velocity.clone(), position: releasePos });
    bus.emit(EVENTS.THROW, {
      side, handle, stallId: this.stallId,
      velocity: velocity.clone(), speed: velocity.length(), position: releasePos,
    });
    return { handle, velocity };
  }

  /**
   * Throw a body with an exact velocity, no hand required.
   * `__cc.stalls.simulateThrow` routes through here.
   * @param {'left'|'right'} side
   * @param {object} handle
   * @param {THREE.Vector3|number[]} velocity
   * @param {THREE.Vector3} [origin] Where to place the object before launch.
   * @returns {{ handle: object, velocity: THREE.Vector3 }|null}
   */
  throwDirect(side, handle, velocity, origin = null) {
    if (!handle || handle.removed) return null;
    const v = Array.isArray(velocity)
      ? new THREE.Vector3(velocity[0], velocity[1], velocity[2])
      : velocity.clone();

    if (this.held[side] !== handle) {
      this.held[side] = handle;
      handle.setKinematic(true);
    }
    if (origin) handle.setPosition(origin);
    // Aim assist and clamps still apply — a scripted throw must exercise the
    // same code path a real one does, or the tests prove nothing.
    let out = this._clampSpeed(v);
    out = this._applyAimAssist(out, handle.position());
    return this.release(side, out);
  }

  // ── Estimator ─────────────────────────────────────────────────────────────

  /**
   * Weighted rolling average of recent hand velocities (GDD 5.3).
   * @param {'left'|'right'} side
   * @returns {THREE.Vector3}
   * @private
   */
  _estimateVelocity(side) {
    const samples = this.buffers[side].all();
    const offset = Math.max(0, Math.min(3, this.profile.releaseFrameOffset ?? 1));
    const usable = offset > 0 ? samples.slice(0, samples.length - offset) : samples;
    if (usable.length < 2) return new THREE.Vector3(0, -0.1, 0);

    /** @type {THREE.Vector3[]} */
    const vels = [];
    for (let i = 1; i < usable.length; i++) {
      const dt = (usable[i].t - usable[i - 1].t) / 1000;
      if (dt < 1e-4) continue;
      const v = usable[i].p.clone().sub(usable[i - 1].p).divideScalar(dt);
      if (Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z)) vels.push(v);
    }
    if (vels.length === 0) return new THREE.Vector3(0, -0.1, 0);

    const frames = Math.max(3, Math.min(12, this.profile.velocitySmoothFrames ?? 6));
    const window = vels.slice(-frames);
    const decay = this.profile.velocityWeightDecay ?? 0.72;

    const out = new THREE.Vector3();
    let weightSum = 0;
    for (let i = 0; i < window.length; i++) {
      // Most recent sample gets weight 1, each older one decays.
      const w = Math.pow(decay, window.length - 1 - i);
      out.addScaledVector(window[i], w);
      weightSum += w;
    }
    if (weightSum > 0) out.divideScalar(weightSum);
    if (!Number.isFinite(out.x) || !Number.isFinite(out.y) || !Number.isFinite(out.z)) {
      return new THREE.Vector3(0, -0.1, 0);
    }
    return out;
  }

  /**
   * Clamp to the profile's speed band. Below minThrowSpeed the object drops
   * limply rather than dribbling forward (GDD 5.3).
   * @param {THREE.Vector3} vel
   * @returns {THREE.Vector3}
   * @private
   */
  _clampSpeed(vel) {
    const speed = vel.length();
    const min = this.profile.minThrowSpeed ?? 1.5;
    const max = this.profile.maxThrowSpeed ?? 20;
    if (speed < 1e-5) return new THREE.Vector3(0, -0.1, 0);
    if (speed < min) return vel.clone().normalize().multiplyScalar(min * 0.25);
    if (speed > max) return vel.clone().normalize().multiplyScalar(max);
    return vel.clone();
  }

  /**
   * Rotate the throw toward a registered target if it falls inside the cone.
   * @param {THREE.Vector3} vel
   * @param {THREE.Vector3} releasePos
   * @returns {THREE.Vector3}
   * @private
   */
  _applyAimAssist(vel, releasePos) {
    const cone = this.profile.aimAssistCone ?? 0;
    const strength = this.profile.aimAssistStrength ?? 0;
    if (cone <= 0 || strength <= 0 || !this.targetFinder) return vel;

    const target = this.targetFinder(releasePos, vel);
    if (!target || !target.position) return vel;

    const speed = vel.length();
    if (speed < 1e-4) return vel;

    const velDir = this._scratch.v.copy(vel).normalize();
    const toTarget = this._scratch.v2.copy(target.position).sub(releasePos);
    if (toTarget.lengthSq() < 1e-6) return vel;
    toTarget.normalize();

    const dot = THREE.MathUtils.clamp(velDir.dot(toTarget), -1, 1);
    const angle = Math.acos(dot);
    if (angle > THREE.MathUtils.degToRad(cone)) return vel;

    const axis = this._scratch.axis.crossVectors(velDir, toTarget);
    if (axis.lengthSq() < 1e-8) return vel; // already aligned
    axis.normalize();

    const q = this._scratch.q.setFromAxisAngle(axis, angle * strength);
    return velDir.clone().applyQuaternion(q).multiplyScalar(speed);
  }

  /**
   * Drive a held body from the hand pose.
   * @private
   */
  _moveHeld(handle, hand) {
    if (!hand) return;
    const entry = this.grabbables.get(handle.id);
    const offset = entry?.opts.holdOffset;
    const pos = this._scratch.v.copy(hand.position);
    if (offset) pos.add(this._scratch.v2.copy(offset).applyQuaternion(hand.quaternion));

    const body = handle.body;
    if (typeof body.setNextKinematicTranslation === 'function') {
      body.setNextKinematicTranslation({ x: pos.x, y: pos.y, z: pos.z });
      const q = entry?.opts.holdRotation
        ? this._scratch.q.copy(hand.quaternion).multiply(entry.opts.holdRotation)
        : hand.quaternion;
      body.setNextKinematicRotation({ x: q.x, y: q.y, z: q.z, w: q.w });
    } else {
      handle.setPosition(pos);
    }
    // Keep the mesh glued to the hand this frame rather than a step behind.
    if (handle.mesh) {
      handle.mesh.position.copy(pos);
      handle.mesh.quaternion.copy(hand.quaternion);
    }
  }
}

/** The one grab system. */
export const grab = new GrabSystem();
